import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, PlusCircle, FilterIcon } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { centrosCostoColumns, columnsExcel } from '@/pages/parametrizacion/centrosCosto/utils/centrosCostoColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { centrosCostoService } from '@/pages/parametrizacion/centrosCosto/utils/centrosCostoService';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const CentrosCostoPage = () => {
  const { toast } = useToast();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchAndSetCentrosCosto = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const { data, totalRecords } = await centrosCostoService.getWithFilter({
        limit: ITEMS_PER_PAGE,
        offset,
        query: filters
      });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron obtener los centros de costo.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchAndSetCentrosCosto();
  }, [fetchAndSetCentrosCosto]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalMode === 'create') {
        const response = await centrosCostoService.create(itemData);
        if (response.code === 201) {
          toast({ title: 'Éxito', description: 'Centro de costo creado correctamente.' });
        } else {
          toast({ title: 'Error', description: response.message || 'Error al crear centro de costo', variant: 'destructive' });
        }
      } else if (modalMode === 'edit') {
        const response = await centrosCostoService.update(currentItem._id, itemData);
        if (response.code === 200) {
          toast({ title: 'Éxito', description: 'Centro de costo actualizado correctamente.' });
        } else {
          toast({ title: 'Error', description: response.message || 'Error al actualizar centro de costo', variant: 'destructive' });
        }
      }
      handleCloseModal();
      fetchAndSetCentrosCosto();
    } catch (error) {
      console.error('Error saving centro de costo:', error);
      const errorMessage = error.message || 'Ocurrió un problema al guardar el centro de costo.';
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (itemToDelete) {
        const response = await centrosCostoService.delete(itemToDelete._id);
        if (response.code === 200) {
          toast({ title: 'Centro de costo eliminado', description: 'El centro de costo fue eliminado correctamente.' });
        } else {
          throw new Error(response.message || 'Error al eliminar centro de costo');
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Ocurrió un problema al eliminar el centro de costo.',
        variant: 'destructive'
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAndSetCentrosCosto();
    }
  };

  const FILTER_FIELDS = useMemo(() => (
    [
      centrosCostoColumns.find((f) => f.id === 'estado'),
      { id: 'createdAt', label: 'Rango de fechas', type: 'daterange', defaultToday: true },
    ].filter(Boolean)
  ), []);

  const columns = [
    { id: 'estado', label: 'Estado', type: 'status' },
    { id: 'nombre', label: 'Nombre' },
    { id: 'descripcion', label: 'Descripción', render: (row) => row?.descripcion || '-' },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <Wallet className="h-6 w-6" />
              Centros de Costos
            </CardTitle>
            <CardDescription>Administra los centros de costos de la empresa.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" /> Filtros
            </Button>
            <Button onClick={() => handleOpenModal('create')}>
              <PlusCircle className="mr-2 h-4 w-4" /> Nuevo Centro de Costo
            </Button>
            <ExportExcelButton
              service={() => centrosCostoService.export(filters)}
              columns={columnsExcel}
              fileName={`centros_costo_${new Date().toISOString().slice(0, 10)}`}
            />
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters
            filters={filters}
            fields={FILTER_FIELDS}
            onRemoveFilter={(key) => setFilters((prev) => {
              const updated = { ...prev };
              delete updated[key];
              return updated;
            })}
          />

          <DataTable
            columns={columns}
            data={displayedData}
            isLoading={isLoading}
            onAction={(actionType, row) => {
              if (actionType === 'edit') return handleOpenModal('edit', row);
              if (actionType === 'delete') return handleDeleteConfirmation(row);
            }}
          />

          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={FILTER_FIELDS}
        onApply={(applied) => { setFilters(applied); setCurrentPage(1); }}
      />

      <AnimatePresence>
        {isModalOpen && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            fields={centrosCostoColumns}
            item={currentItem}
            title={modalMode === 'create' ? 'Crear Nuevo Centro de Costo' : 'Editar Centro de Costo'}
            mode={modalMode}
          />
        )}
      </AnimatePresence>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar centro de costo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el centro de costo "{itemToDelete?.nombre}" permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default CentrosCostoPage;
